import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import { useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { GOOGLE_DOCS } from '@/lib/docs'
import { formatDate } from '@/lib/format'
import { Empty, ErrorState, Loading } from '@/ui/states'

/**
 * Search performance — the SEO module's first real screen (S-B22a).
 *
 * The figures, and beside them which property they come from. Search Console
 * has two kinds of property holding different data — `sc-domain:example.com`
 * covers every scheme and subdomain, `https://www.example.com/` covers exactly
 * that prefix — so AugRank's match is a ranked guess and the tenant is entitled
 * to correct it. **The correction is made on Settings › Connections** since the
 * settings consolidation's second batch (2026-09-15) — the owner's rule that a
 * setting has one home outranked this file's earlier argument for keeping the
 * picker beside the numbers. What stays beside the numbers is the property's
 * name, the warning when there is none, and the link.
 *
 * `docs/GOOGLE-OAUTH-VERIFICATION.md` §5 shot 5 is this screen: the picker
 * listing real properties, and clicks / impressions / CTR / average position with
 * the query table underneath.
 *
 * **The empty state is a real state and appears often.** A property registered
 * last week genuinely has no data, and Search Console's own figures lag by two to
 * three days. Rendering four zeroes would be a lie dressed as a measurement, so
 * `has_data` drives a sentence that says what is actually happening.
 */
export function SearchPerformance(): JSX.Element {
  const brand = useBrand()
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])

  // The first site until somebody chooses another. Derived rather than stored in
  // an effect: an effect that set state from a fetch would render once with no
  // selection and once with one, and the second render would refetch.
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null
  const hasProperty = site !== null && site.gsc_property !== null && site.gsc_property !== ''

  const performance = useQuery(
    (signal) =>
      selectedId === null || !hasProperty
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/performance', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId, hasProperty],
  )

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Search performance</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {sites.state.status === 'loading' ? (
        <Loading label="Loading your sites" />
      ) : sites.state.status === 'error' ? (
        <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
      ) : site === null ? (
        <Empty
          title="No site yet"
          body={`Add your website and ${brand.productName} can start reading how it performs in Google search.`}
          action={<Link to="/onboarding/site">Add your site</Link>}
        />
      ) : (
        <div className="ar-stack">
          <section className="ar-card">
            <div className="ar-card__header">
              <h2>{site.domain}</h2>
              <span className="ar-card__spacer" />
              {hasProperty ? (
                <span className="ar-mono ar-text-body-sm">{site.gsc_property}</span>
              ) : (
                <span className="ar-badge ar-badge--warning">No property chosen</span>
              )}
            </div>

            {/*
              `.ar-card__body` carries the inset. Without it the prose starts at
              the card's border while the heading above it starts a
              `--ar-space-6` in, and the two disagree about where the card's
              left edge is — which is what made this screen read as misaligned.
            */}
            <div className="ar-card__body">
              {hasProperty ? (
                <p className="ar-text-body-sm ar-muted">
                  Reading the Search Console property above.{' '}
                  {site.verified_at !== null && site.verified_at !== undefined
                    ? `Last confirmed ${formatDate(site.verified_at)}.`
                    : null}{' '}
                  <Link to="/settings/connections">Change in Settings →</Link>
                </p>
              ) : (
                <>
                  <p className="ar-empty ar-empty--inline">
                    {brand.productName} could not match this site to a Search Console property in
                    the Google account you connected. Pick the right one, or connect a different
                    account.{' '}
                    <a
                      className="ar-link"
                      href={GOOGLE_DOCS.noMatchingProperty}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Why this happens, and how to verify a site ↗
                    </a>
                  </p>
                  <Link className="ar-btn ar-btn--sm" to="/settings/connections">
                    Choose a property in Settings →
                  </Link>
                </>
              )}
            </div>
          </section>

          {!hasProperty ? null : performance.state.status === 'loading' ? (
            <Loading label="Reading Search Console" />
          ) : performance.state.status === 'error' ? (
            <ErrorState failure={performance.state.failure} onRetry={performance.refetch} />
          ) : performance.state.data === null ? null : performance.state.data.has_data ? (
            <Figures data={performance.state.data} />
          ) : (
            <Empty
              title="Google has no data for this property yet"
              body={
                'Search Console needs a few days of traffic before it reports anything, and its ' +
                'figures always lag by two to three days. Nothing is wrong — check back later ' +
                'this week.'
              }
              action={
                <a
                  className="ar-link"
                  href={GOOGLE_DOCS.whyIsItEmpty}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Why a working connection is still empty ↗
                </a>
              }
            />
          )}

          <TrafficPanel siteId={site.id} propertyId={site.ga4_property_id ?? null} />
        </div>
      )}
    </>
  )
}

/**
 * Google Analytics, beside the search figures. Shot 7 of the demonstration video.
 *
 * **Below the Search Console figures rather than on its own screen**, and that
 * placement is the feature. §5's narration is *"so the customer can see whether a
 * page that gained impressions also gained visits"* — a question nobody can answer
 * by navigating between two tabs. Impressions rising while sessions do not is the
 * single most useful thing this product can show a small business, and it is only
 * visible when both are in one viewport.
 *
 * **Absent, not broken, when Analytics was not granted.** Google's granular
 * consent renders one tick box per scope, so a tenant who took Search Console and
 * declined Analytics made a choice. The API answers 409 with a sentence naming
 * that permission, and this renders it as a quiet offer to reconnect rather than
 * as an error — the same treatment the DNS-token route gets under partial consent.
 */
function TrafficPanel({
  siteId,
  propertyId,
}: {
  siteId: string
  propertyId: string | null
}): JSX.Element {
  const brand = useBrand()
  const chosen = propertyId !== null && propertyId !== ''

  const traffic = useQuery(
    (signal) =>
      !chosen
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/traffic', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId, chosen],
  )

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Traffic</h2>
        <span className="ar-card__spacer" />
        {chosen ? (
          <span className="ar-mono ar-text-body-sm">Analytics property {propertyId}</span>
        ) : (
          <span className="ar-badge ar-badge--warning">No Analytics property chosen</span>
        )}
      </div>

      <div className="ar-card__body">
        {!chosen ? (
          <>
            <p className="ar-empty ar-empty--inline">
              Connect a Google Analytics property and you will see whether the pages gaining
              impressions above are actually gaining visits. The list comes from the Google account
              you connected, so it has to already have access to the property in Analytics.{' '}
              <a
                className="ar-link"
                href={GOOGLE_DOCS.analytics}
                target="_blank"
                rel="noreferrer noopener"
              >
                How to choose the right property ↗
              </a>
            </p>
            <Link className="ar-btn ar-btn--sm" to="/settings/connections">
              Choose an Analytics property in Settings →
            </Link>
          </>
        ) : (
          <p className="ar-text-body-sm ar-muted">
            Reading Google Analytics. <Link to="/settings/connections">Change in Settings →</Link>
          </p>
        )}

        {!chosen ? null : traffic.state.status === 'loading' ? (
          <Loading label="Reading Google Analytics" />
        ) : traffic.state.status === 'error' ? (
          <ErrorState failure={traffic.state.failure} onRetry={traffic.refetch} />
        ) : traffic.state.data === null ? null : traffic.state.data.has_data ? (
          <>
            <div className="app-stats">
              <Stat label="Sessions" value={traffic.state.data.sessions.toLocaleString('en-IN')} />
              <Stat
                label="Engaged sessions"
                value={traffic.state.data.engaged_sessions.toLocaleString('en-IN')}
              />
              <Stat label="Users" value={traffic.state.data.total_users.toLocaleString('en-IN')} />
              <Stat
                label="Key events"
                value={traffic.state.data.key_events.toLocaleString('en-IN')}
              />
            </div>
            <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
              {formatDate(traffic.state.data.start_date)} —{' '}
              {formatDate(traffic.state.data.end_date)}, straight from Google Analytics.{' '}
              {(traffic.state.data.engagement_rate * 100).toFixed(0)}% of sessions were engaged.
              Read-only — {brand.productName} never writes to Analytics.
            </p>
          </>
        ) : (
          <Empty
            title="Google Analytics has no data for this property yet"
            body={
              'Either the property was created recently, or its tag is not installed on the site. ' +
              'Nothing is wrong with the connection — Analytics simply has nothing to report yet.'
            }
            action={
              <a
                className="ar-link"
                href={GOOGLE_DOCS.whyIsItEmpty}
                target="_blank"
                rel="noreferrer noopener"
              >
                What Analytics needs before it reports ↗
              </a>
            }
          />
        )}
      </div>
    </section>
  )
}

/** The four figures and the query table. Shot 5 of the demonstration video. */
function Figures({
  data,
}: {
  data: {
    clicks: number
    impressions: number
    ctr: number
    position: number
    start_date: string
    end_date: string
    rows: {
      query: string
      clicks: number
      impressions: number
      ctr: number
      position: number
    }[]
  }
}): JSX.Element {
  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>
            {formatDate(data.start_date)} — {formatDate(data.end_date)}
          </h2>
        </div>
        <div className="ar-card__body">
          <div className="app-stats">
            <Stat label="Clicks" value={data.clicks.toLocaleString('en-IN')} />
            <Stat label="Impressions" value={data.impressions.toLocaleString('en-IN')} />
            <Stat label="CTR" value={`${(data.ctr * 100).toFixed(1)}%`} />
            <Stat label="Average position" value={data.position.toFixed(1)} />
          </div>
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            Straight from Google Search Console. Its data lags by two to three days, so the window
            ends earlier than today — that way the numbers do not change under you on a reload.
          </p>
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Top queries</h2>
        </div>
        {/*
          The table stays outside a body and full-bleed — its cells carry their
          own padding and its row hover is meant to reach both card edges — so
          only the empty state that replaces it needs the inset.
        */}
        {data.rows.length === 0 ? (
          <div className="ar-card__body">
            <p className="ar-empty ar-empty--inline">
              Google reported no individual search terms for this window, which happens on a
              property with very little traffic.
            </p>
          </div>
        ) : (
          <div className="ar-table-wrap" tabIndex={0}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th scope="col">Query</th>
                  <th scope="col">Clicks</th>
                  <th scope="col">Impressions</th>
                  <th scope="col">CTR</th>
                  <th scope="col">Position</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.query}>
                    <td>{row.query}</td>
                    <td>{row.clicks.toLocaleString('en-IN')}</td>
                    <td>{row.impressions.toLocaleString('en-IN')}</td>
                    <td>{(row.ctr * 100).toFixed(1)}%</td>
                    <td>{row.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="app-stat">
      <span className="app-stat__label">{label}</span>
      <span className="app-stat__value">{value}</span>
    </div>
  )
}
